<script setup lang="ts">
/**
 * The one window that says what you earned.
 *
 * Rendered once, near the root, and driven entirely by `state/reward-popup` — it
 * has no props and knows nothing about quests or battles: it is handed a
 * heading, a subject and a list of amounts, and it has no way to ask which
 * caller sent them. It has **no live caller today**: the sortie's "Battle
 * report" was retired when the result screen started showing the payout in the
 * arena, and the daily-quest claim — its only caller after that — moved to
 * `ui/NotificationBanner.vue`, a non-blocking strip under the title bar, for
 * exactly the reason this one holds the screen: a claim pressed from the
 * garage floor is not a moment that needs a focus-holding scrim, and this
 * component's whole design is a scrim held on purpose. It stays mounted and
 * caller-blind so the next payout that genuinely wants a receipt worth pausing
 * for — a moment, not a garage-floor click — costs no second component.
 *
 * Three things about it are load-bearing, and two of them are the mirror image of
 * `ConfirmPurchase.vue`'s.
 *
 *   **Every way out is the same way out.** Escape, Return, the backdrop and the
 *   button all dismiss. The opposite of the confirmation dialog, and deliberately
 *   so: there the safe answer was *no* and a stray Return had to be harmless,
 *   here the money has already moved and there is nothing to be careful about.
 *   The dismiss button therefore *is* autofocused.
 *
 *   **The numbers are formatted here, not by the caller.** Otherwise every call
 *   site carries its own idea of what a number of credits looks like, and one of
 *   them eventually disagrees.
 *
 *   **An informational figure is drawn quietly.** A receipt may state a count
 *   beside the credits, and showing "2 deaths" in the same accent as
 *   "+600 credits" would be claiming somebody was paid in deaths.
 */
import { nextTick, ref, watch } from 'vue';

import { dismissRewards, rewardReport } from '@/state/reward-popup';
import { formatCredits } from '@/garage/stat-display';

const dismissButton = ref<HTMLButtonElement | null>(null);

watch(rewardReport, async (report) => {
  if (!report) return;
  await nextTick();
  dismissButton.value?.focus();
});

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopPropagation();
    dismissRewards();
  }
}

/** The unit shown after an amount. `plain` has none. */
function unitOf(kind: string): string {
  if (kind === 'credits') return 'cr';
  if (kind === 'diamonds') return '◆';
  if (kind === 'xp') return 'xp';
  return '';
}
</script>

<template>
  <div
    v-if="rewardReport"
    class="scrim"
    data-test="reward-popup"
    @click.self="dismissRewards()"
    @keydown="onKeydown"
  >
    <div class="dialog" role="alertdialog" aria-modal="true" aria-labelledby="reward-title">
      <h2 id="reward-title">{{ rewardReport.heading }}</h2>

      <p class="subject" data-test="reward-subject">{{ rewardReport.subject }}</p>
      <p v-if="rewardReport.detail" class="detail">{{ rewardReport.detail }}</p>

      <ul class="rewards" data-test="reward-list">
        <li
          v-for="(reward, index) in rewardReport.rewards"
          :key="`${reward.label}-${index}`"
          :class="{ quiet: reward.informational }"
        >
          <span class="label">{{ reward.label }}</span>
          <span class="amount">
            <template v-if="!reward.informational">+</template>{{
              formatCredits(Math.round(reward.amount))
            }}
            <em v-if="unitOf(reward.kind)">{{ unitOf(reward.kind) }}</em>
          </span>
        </li>
      </ul>

      <div class="actions">
        <button
          ref="dismissButton"
          type="button"
          class="accept"
          data-test="reward-dismiss"
          @click="dismissRewards()"
        >
          {{ rewardReport.dismissLabel ?? 'Good' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/*
 * Over everything, and one below the purchase confirmation.
 *
 * They cannot both be open — a claim shows this only after its request has
 * returned, by which time no purchase is being confirmed — but the ordering is
 * stated rather than left to chance, because the one modal that must always win
 * is the one asking to spend money.
 */
.scrim {
  position: fixed;
  inset: 0;
  z-index: 99;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(4, 6, 9, 0.68);
  backdrop-filter: blur(3px);
}

.dialog {
  width: min(22rem, 100%);
  padding: 1.1rem 1.2rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 5px;
  background: var(--steel-800);
  box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.6);
}

.dialog h2 {
  margin: 0 0 0.7rem;
  color: var(--accent);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.subject {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.35;
  font-weight: 600;
}

.detail {
  margin: 0.25rem 0 0;
  color: var(--steel-400);
  font-size: 0.78rem;
  line-height: 1.4;
}

/* The list is the point of the window, so it is the largest thing in it. */
.rewards {
  margin: 0.9rem 0 1.1rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.rewards li {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.35rem 0.5rem;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.04);
}

.label {
  color: var(--steel-400);
  font-size: 0.74rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.amount {
  color: var(--accent);
  font-size: 1.15rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.amount em {
  font-style: normal;
  font-size: 0.72rem;
  font-weight: 500;
  opacity: 0.7;
}

/* Stated, not celebrated. See the note at the top. */
.rewards li.quiet {
  background: none;
  padding-top: 0;
  padding-bottom: 0;
}

.rewards li.quiet .amount {
  color: var(--steel-200, #d7dde3);
  font-size: 0.9rem;
  font-weight: 600;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.accept {
  padding: 0.5rem 1.6rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: var(--accent);
  color: #14181d;
  font-size: 0.82rem;
  font-weight: 700;
}
</style>
